import type { QueryClient } from '@tanstack/react-query';
import { issueOptions, type IssueFetchParams } from '@shared/api';

import config from '@/config';
import { Issue, USER_ROLE, clone } from '@/constants';
import type { Db } from '@shared/storage';
import { buildStyles } from '@/lib/article-renderer/core/style-builder';

import { saveDownloadedIssue } from './downloaded-issue';

/**
 * Re-fetches a downloaded issue's content from the network, replaying the
 * *exact same* TanStack Query the original download captured
 * (`Issue.downloadQuery`, set by `saveDownloadedIssue`'s caller at download
 * time) -- driven by the offline reader's "refresh content" action
 * (`widgets/issue-reader/menu/menu-button.tsx`'s `RefreshContentButton`).
 *
 * `queryClient.fetchQuery`, not a bare `fetch`: this is what makes "update
 * the current TanStack Query data" true for free -- a successful fetch
 * writes into the query cache under this exact key, so a reader who later
 * opens the *online* `/issues/$id` route for the same issue also sees the
 * refreshed copy, not a stale one this action bypassed.
 *
 * Styles are rebuilt (`buildStyles`, same as `useIssue` does for the online
 * reader) and re-saved through `saveDownloadedIssue`, which upserts the ones
 * the refreshed issue uses and drops the ones it no longer does (unless
 * another downloaded issue still uses them) -- a refresh is exactly the kind
 * of event that can bring a new/changed style.
 *
 * Image pinning/pruning is deliberately *not* done here: that needs
 * `useServiceWorker()`'s SW messaging, a React hook this plain async
 * function can't call. The caller (`RefreshContentButton`) diffs this
 * function's returned issue's images against the pre-refresh ones itself
 * and triggers `pinIssueAssets`/`pruneRemovedIssueAssets` from the
 * component.
 *
 * Throws on any failure (network, no issue in the response, a rejected
 * write) -- unlike this entity's other functions, which are best-effort:
 * the whole point of a refresh the reader explicitly asked for is knowing
 * whether it actually worked, so the caller can show a real error instead
 * of a silent no-op.
 */
export async function refreshDownloadedIssue(args: {
	db: Db;
	issueId: string;
	downloadQuery: Issue['downloadQuery'];
	queryClient: QueryClient;
}): Promise<Issue> {
	const { db, issueId, downloadQuery, queryClient } = args;
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	const data = await queryClient.fetchQuery(
		issueOptions(issueId, {
			isOnline: true,
			isAdmin,
			ttlMinutes: config.app?.cache?.ttl?.getIssue,
			limit: downloadQuery?.limit,
			sortKey: downloadQuery?.sortKey as IssueFetchParams['sortKey'],
			reverse: downloadQuery?.reverse,
			onSessionExpired: () => {},
		}),
	);

	if (!data.issue) {
		throw new Error(`Issue ${issueId} was not found on refresh`);
	}

	const freshIssue = clone(data.issue);
	if (freshIssue.styles) {
		freshIssue.styles = buildStyles(freshIssue.styles);
	}

	await saveDownloadedIssue(db, freshIssue, freshIssue.articles ?? [], {
		styles: freshIssue.styles ?? [],
		downloadQuery,
	});

	return freshIssue;
}
